import { state, set, subscribe } from '../../state/store.js';
import { formatPrice, escapeHtml } from '../../lib/format.js';
import { addLine, removeLine } from '../../lib/shopify.js';
import { UPSELL_STOCK } from '../../config/settings.js';

const $list = () => document.getElementById('upsell-list');

function isAdded(id) {
  // Cart lines carry the variant GID as `id`, matching what we expose for
  // upsells; so a variant present in the cart counts as "already added".
  return state.cart.lines.some((l) => l.id === id);
}

/**
 * Build the storefront product URL for an upsell. Returns null when we
 * don't have enough info (e.g. recommendations missing a handle, or the
 * customer arrived without a storeName param) — the card then renders
 * non-clickable instead of a broken link.
 */
function productUrl(p) {
  if (!p?.handle) return null;
  const store = state.shop?.storeName;
  if (!store) return null;
  const origin = /^https?:\/\//.test(store) ? store : `https://${store}`;
  return `${origin.replace(/\/+$/, '')}/products/${encodeURIComponent(p.handle)}`;
}

/** Wraps the clickable inner content (thumb / name / price) in either an
 *  anchor or a div, depending on whether we have a target URL. The Add
 *  button is rendered OUTSIDE this wrapper so clicking it doesn't navigate. */
function linkOpen(url) {
  return url
    ? `<a class="upsell-link" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">`
    : '<div class="upsell-link">';
}
function linkClose(url) { return url ? '</a>' : '</div>'; }

/**
 * Whether this upsell can still be added. The server already filters out
 * anything unsellable (see server/routes/recommendations.js), so this is a
 * client-side backstop for the window between render and click — and for the
 * offline/dev fallback list, which carries no stock metadata at all.
 *
 * `stock === null`/undefined means the variant doesn't track inventory, which
 * is unlimited, not zero.
 */
function isSellable(p) {
  if (!p) return false;
  if (p.availableForSale === false) return false;
  if (typeof p.stock === 'number' && p.stock <= 0) {
    // Zero stock is only sellable as a backorder, and only if we allow those.
    return !!p.backorder && UPSELL_STOCK.ALLOW_BACKORDER;
  }
  return true;
}

function buildCard(p) {
  const added = isAdded(p.id);
  const sellable = isSellable(p);
  const thumb = p.image
    ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.name)}">`
    : `<div class="ph"></div>`;
  const url = productUrl(p);
  const variantLine = p.variant
    ? `<div class="upsell-variant">${escapeHtml(p.variant)}</div>`
    : '';

  // Sold out: keep the card (it still links to the storefront product page)
  // but make the action inert rather than letting the customer click into a
  // guaranteed failure.
  const btn = sellable
    ? `<button type="button" class="add-btn ${added ? 'is-added' : ''}" data-id="${escapeHtml(p.id)}">${added ? '✓ Додано' : 'Додати'}</button>`
    : `<button type="button" class="add-btn is-unavailable" disabled aria-disabled="true">Немає в наявності</button>`;

  return `
    <div class="upsell-card${sellable ? '' : ' is-unavailable'}">
      ${linkOpen(url)}
        <div class="upsell-thumb">${thumb}</div>
        <p class="upsell-name">${escapeHtml(p.name)}</p>
        ${variantLine}
        <span class="upsell-price">${formatPrice(p.price)}</span>
      ${linkClose(url)}
      ${btn}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Carousel controls
//
// The row has always been horizontally scrollable, but with the scrollbar
// hidden that only helped touch and trackpad users — a desktop mouse had no
// way to reach cards 4+. This adds prev/next arrows and click-drag, without
// touching the touch path (native swipe stays exactly as it was).
// ---------------------------------------------------------------------------

/** Honour the OS "reduce motion" setting for the arrow scroll animation. */
function scrollBehavior() {
  try {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  } catch (_) { return 'smooth'; }
}

/** Width of one card plus the flex gap, measured from the DOM (never hardcoded). */
function stepSize(list) {
  const card = list.querySelector('.upsell-card');
  if (!card) return list.clientWidth || 0;
  const gap = parseFloat(getComputedStyle(list).columnGap || getComputedStyle(list).gap || '0') || 0;
  return card.offsetWidth + gap;
}

/** Scroll by as many whole cards as fit, so a card never lands half-clipped. */
function pageSize(list) {
  const step = stepSize(list);
  if (!step) return list.clientWidth;
  return Math.max(1, Math.floor(list.clientWidth / step)) * step;
}

/**
 * Show the arrows only when there is somewhere to scroll, and disable each one
 * at its end. `hidden` on the container keeps it out of the a11y tree entirely
 * when the row fits, so screen-reader users aren't offered dead controls.
 */
function syncCarouselNav() {
  const list = $list();
  const nav = document.getElementById('upsell-nav');
  if (!list || !nav) return;
  const overflow = list.scrollWidth - list.clientWidth;
  // 2px slack: sub-pixel layout can leave a phantom 0.5px of overflow.
  if (overflow <= 2) { nav.hidden = true; return; }
  nav.hidden = false;
  const max = list.scrollWidth - list.clientWidth;
  const prev = nav.querySelector('[data-dir="prev"]');
  const next = nav.querySelector('[data-dir="next"]');
  if (prev) prev.disabled = list.scrollLeft <= 2;
  if (next) next.disabled = list.scrollLeft >= max - 2;
}

let carouselWired = false;

function setupCarousel() {
  const list = $list();
  const nav = document.getElementById('upsell-nav');
  if (!list || carouselWired) return;
  carouselWired = true;

  nav?.querySelectorAll('.upsell-arrow').forEach((btn) => {
    btn.addEventListener('click', () => {
      const dir = btn.dataset.dir === 'prev' ? -1 : 1;
      list.scrollBy({ left: dir * pageSize(list), behavior: scrollBehavior() });
    });
  });

  // --- Click-and-drag (mouse only) -----------------------------------------
  // Guarded on pointerType so we never interfere with native touch scrolling,
  // which already works and is smoother than anything we'd emulate.
  let dragging = false;
  let startX = 0;
  let startScroll = 0;
  let moved = 0;

  list.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    dragging = true;
    moved = 0;
    startX = e.clientX;
    startScroll = list.scrollLeft;
  });

  list.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const dx = e.clientX - startX;
    if (Math.abs(dx) > moved) moved = Math.abs(dx);
    if (moved <= 3) return;               // tolerate a shaky click
    // Capture only once we're sure it's a drag, so a plain click on a card
    // still reaches the <a>/<button> inside it.
    if (!list.hasPointerCapture?.(e.pointerId)) {
      try { list.setPointerCapture(e.pointerId); } catch (_) {}
      // scroll-snap fights a manual drag; disable it for the duration.
      list.classList.add('is-dragging');
    }
    e.preventDefault();
    list.scrollLeft = startScroll - dx;
  });

  const endDrag = (e) => {
    if (!dragging) return;
    dragging = false;
    list.classList.remove('is-dragging');
    try { list.releasePointerCapture?.(e.pointerId); } catch (_) {}
  };
  list.addEventListener('pointerup', endDrag);
  list.addEventListener('pointercancel', endDrag);

  // A drag ends in a click on whatever card was under the cursor — swallow it,
  // otherwise dragging navigates to a product page or fires "Додати".
  list.addEventListener('click', (e) => {
    if (moved > 5) {
      e.preventDefault();
      e.stopPropagation();
      moved = 0;
    }
  }, true);

  // --- Keyboard -------------------------------------------------------------
  // The row is tabbable (tabindex="0" in index.html) because it is a scrollable
  // region; arrow keys must therefore move it.
  list.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const dir = e.key === 'ArrowLeft' ? -1 : 1;
    list.scrollBy({ left: dir * stepSize(list), behavior: scrollBehavior() });
  });

  list.addEventListener('scroll', syncCarouselNav, { passive: true });
  try {
    new ResizeObserver(syncCarouselNav).observe(list);
  } catch (_) {
    window.addEventListener('resize', syncCarouselNav);
  }
}

/**
 * Inline stock message under the carousel. Created lazily so the markup in
 * index.html doesn't need a dedicated slot.
 */
function upsellErrorEl() {
  const list = $list();
  if (!list?.parentNode) return null;
  let el = document.getElementById('upsell-error');
  if (!el) {
    el = document.createElement('div');
    el.id = 'upsell-error';
    el.className = 'upsell-error';
    list.parentNode.insertBefore(el, list.nextSibling);
  }
  return el;
}

function showUpsellError(message) {
  const el = upsellErrorEl();
  if (!el) return;
  el.textContent = message || 'Цей товар зараз недоступний.';
  // Must be an explicit value: the .upsell-error class rule is `display:none`,
  // so assigning '' would clear the inline style and let the class win again,
  // leaving the message permanently invisible.
  el.style.display = 'block';
}

function clearUpsellError() {
  const el = document.getElementById('upsell-error');
  if (el) el.style.display = 'none';
}

/**
 * Mark an upsell unsellable in local state so it can't be clicked again this
 * session, even though the server list said it was available at load time.
 */
function markUnavailable(id) {
  const next = (state.upsells || []).map((p) =>
    p.id === id ? { ...p, availableForSale: false, stock: 0, backorder: false } : p
  );
  set({ upsells: next });
}

function render() {
  const root = $list();
  if (!root) return;
  const products = state.upsells || [];

  if (products.length === 0) {
    root.innerHTML = '';
    const nav = document.getElementById('upsell-nav');
    if (nav) nav.hidden = true;
    return;
  }

  root.className = 'upsell-list is-carousel';
  root.innerHTML = products.map(buildCard).join('');
  setupCarousel();
  // Card widths are known only after paint; re-check on the next frame so the
  // arrows appear/disappear against real measurements.
  requestAnimationFrame(syncCarouselNav);

  root.querySelectorAll('.add-btn:not([disabled])').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      const p = (state.upsells || []).find((x) => x.id === id);
      if (!p) return;
      // Re-check at click time: the list may have been rendered minutes ago.
      if (!isAdded(id) && !isSellable(p)) {
        showUpsellError('На жаль, цей товар щойно розкупили.');
        markUnavailable(id);
        return;
      }

      const shop   = state.shop?.shopifyDomain;
      const cartId = state.cart?.id;
      const liveMode = !!(shop && cartId);

      if (liveMode) {
        // Mutate the real Shopify cart so the discount + totals stay correct.
        btn.disabled = true;
        const wasAdded = isAdded(id);
        btn.textContent = wasAdded ? 'Видалення…' : 'Додавання…';
        clearUpsellError();
        try {
          let updated;
          if (wasAdded) {
            const line = state.cart.lines.find((l) => l.id === id);
            if (line?.lineKey) {
              updated = await removeLine({ shop, cartId, lineId: line.lineKey });
            }
          } else {
            updated = await addLine({
              shop, cartId,
              merchandiseId: p.variantId || p.id,
              quantity: 1,
            });
          }
          if (updated) set({ cart: { ...state.cart, ...updated } });
        } catch (err) {
          console.error('[upsell] mutate failed', err);
          // 409 = Shopify refused on stock grounds. Tell the customer, flip the
          // card to unavailable, and adopt the corrected cart the server sent.
          if (err.status === 409) {
            showUpsellError(err.message);
            markUnavailable(id);
            if (err.cart) set({ cart: { ...state.cart, ...err.cart } });
            return; // render() from the state change repaints the button
          }
          btn.textContent = wasAdded ? '✓ Додано' : 'Додати';
        } finally {
          btn.disabled = false;
        }
        return;
      }

      // Offline / dev fallback: mutate local cart only.
      let lines = state.cart.lines.map((l) => ({ ...l }));
      if (isAdded(id)) {
        lines = lines.filter((l) => l.id !== id);
      } else {
        lines.push({
          id: p.id, name: p.name, variant: '', price: p.price, qty: 1,
          image: p.image, placeholder: p.placeholder,
        });
      }
      const subtotal = lines.reduce((a, x) => a + x.price * x.qty, 0);
      set({ cart: { ...state.cart, lines, subtotal } });
    });
  });
}

export function initUpsell() {
  render();
  subscribe(['cart', 'upsells'], render);
}
